import React, { useState, useEffect } from 'react';
import { 
    CircularProgress, Paper, Typography, Box, Chip, Grid,
    Card, CardContent, Alert, LinearProgress 
} from '@mui/material';
import { 
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, 
    ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar,
    LineChart, Line, Legend
} from 'recharts';
import api from "../../../services/api";
import { red, green, orange, blue, grey } from '@mui/material/colors';
import SignalStrengthIcon from '@mui/icons-material/SignalWifi4Bar';
import SignalOffIcon from '@mui/icons-material/SignalWifiOff';
import WarningIcon from '@mui/icons-material/Warning';

// =============================================================================
// COMPONENTE: Status em Tempo Real
// =============================================================================
export function RealTimeStatus() {
    const [stats, setStats] = useState({
        online: 0,
        offline: 0,
        weak_signal: 0,
        total: 0
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await api.get('/provisioning/dashboard/signal_status');
                setStats(response.data);
            } catch (error) {
                console.error('Erro ao buscar status:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
        const interval = setInterval(fetchStats, 30000); // Atualiza a cada 30s
        
        return () => clearInterval(interval);
    }, []);

    if (loading) return <LoadingCard height={200} />;

    const onlinePercentage = stats.total > 0 ? ((stats.online / stats.total) * 100).toFixed(1) : 0;

    return (
        <Card elevation={2}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" sx={{ fontFamily: 'Montserrat' }}>
                        Status da Rede
                    </Typography>
                    <Chip 
                        label="Tempo Real" 
                        color="success" 
                        size="small"
                        sx={{ animation: 'pulse 2s infinite' }}
                    />
                </Box>
                
                <Grid container spacing={3}>
                    <Grid item xs={6} sm={3}>
                        <StatusCard
                            icon={<SignalStrengthIcon sx={{ fontSize: 32, color: green[600] }} />}
                            value={stats.online}
                            label="Online"
                            color={green[600]}
                            percentage={onlinePercentage}
                        />
                    </Grid>
                    
                    <Grid item xs={6} sm={3}>
                        <StatusCard
                            icon={<SignalOffIcon sx={{ fontSize: 32, color: red[600] }} />}
                            value={stats.offline}
                            label="Offline"
                            color={red[600]}
                        />
                    </Grid>
                    
                    <Grid item xs={6} sm={3}>
                        <StatusCard
                            icon={<WarningIcon sx={{ fontSize: 32, color: orange[600] }} />}
                            value={stats.weak_signal}
                            label="Sinal Fraco"
                            color={orange[600]}
                        />
                    </Grid>
                    
                    <Grid item xs={6} sm={3}>
                        <StatusCard
                            value={stats.total}
                            label="Total"
                            color={blue[600]}
                        />
                    </Grid>
                </Grid>

                <Box mt={3}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">
                            Disponibilidade da Rede
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                            {onlinePercentage}%
                        </Typography>
                    </Box>
                    <LinearProgress 
                        variant="determinate" 
                        value={parseFloat(onlinePercentage)} 
                        sx={{ 
                            height: 8, 
                            borderRadius: 4,
                            backgroundColor: grey[300],
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: onlinePercentage > 95 ? green[600] : 
                                               onlinePercentage > 90 ? orange[600] : red[600],
                                borderRadius: 4
                            }
                        }}
                    />
                </Box>
            </CardContent>
        </Card>
    );
}

// =============================================================================
// COMPONENTE: Gráfico de Qualidade de Sinal
// =============================================================================
export function SignalQualityChart() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await api.get('/provisioning/dashboard/signal_quality_trend');
                setData(response.data);
            } catch (error) {
                console.error('Erro ao buscar dados:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) return <LoadingCard height={350} />;

    return (
        <ChartContainer title="Qualidade do Sinal (24h)">
            <ResponsiveContainer height={300} width="100%">
                <AreaChart data={data}>
                    <defs>
                        <linearGradient id="signalGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={blue[600]} stopOpacity={0.8}/>
                            <stop offset="95%" stopColor={blue[600]} stopOpacity={0.1}/>
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" />
                    <YAxis domain={[-35, -10]} />
                    <Tooltip 
                        formatter={(value) => [`${value} dBm`, 'Sinal Médio']}
                        labelFormatter={(label) => `Horário: ${label}`}
                    />
                    <Area 
                        type="monotone" 
                        dataKey="avg_signal" 
                        stroke={blue[600]} 
                        fill="url(#signalGradient)"
                        strokeWidth={2}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </ChartContainer>
    );
}

// =============================================================================
// COMPONENTE: ONUs Problemáticas
// =============================================================================
export function ProblematicONUs() {
    const [onus, setOnus] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await api.get('/provisioning/dashboard/problematic_onus');
                setOnus(response.data);
            } catch (error) {
                console.error('Erro ao buscar ONUs:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) return <LoadingCard height={350} />;

    return (
        <ChartContainer title="ONUs com Problemas">
            <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                {onus.length === 0 ? (
                    <Alert 
                        severity="success" 
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 200
                        }}
                    >
                        🎉 Nenhum problema detectado na rede!
                    </Alert>
                ) : (
                    onus.map((onu, index) => (
                        <Box 
                            key={index} 
                            display="flex" 
                            justifyContent="space-between" 
                            alignItems="center"
                            py={1.5}
                            px={1}
                            borderBottom={index < onus.length - 1 ? `1px solid ${grey[200]}` : 'none'}
                            sx={{
                                '&:hover': {
                                    backgroundColor: grey[50],
                                    borderRadius: 1
                                }
                            }}
                        >
                            <Box>
                                <Typography variant="body2" fontWeight="bold">
                                    {onu.description || `Cliente ${onu.sn.slice(-8)}`}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    SN: {onu.sn}
                                </Typography>
                            </Box>
                            
                            <Box textAlign="right">
                                <Chip 
                                    label={
                                        onu.status === 'offline' ? 'Offline' :
                                        onu.status === 'weak' ? 'Sinal Fraco' : 'Instável'
                                    }
                                    color={
                                        onu.status === 'offline' ? 'error' :
                                        onu.status === 'weak' ? 'warning' : 'default'
                                    }
                                    size="small"
                                />
                                {onu.rx && (
                                    <Typography variant="caption" display="block" color="textSecondary" mt={0.5}>
                                        {onu.rx} dBm
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    ))
                )}
            </Box>
        </ChartContainer>
    );
}

// =============================================================================
// COMPONENTE: Distribuição de Sinais
// =============================================================================
export function SignalDistribution() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await api.get('/provisioning/dashboard/signal_distribution');
                setData(response.data);
            } catch (error) {
                console.error('Erro ao buscar distribuição:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) return <LoadingCard height={350} />;

    // Cor por SIGNIFICADO (não por índice) — antes "Offline" reciclava o
    // verde do "Excelente" (offline parecia ótimo).
    const COLOR_BY_NAME = {
        "Excelente": green[600],
        "Bom": blue[600],
        "Regular": orange[600],
        "Ruim": red[600],
        "Offline": grey[500],
    };

    return (
        <ChartContainer title="Distribuição da Qualidade">
            <ResponsiveContainer height={300} width="100%">
                <PieChart>
                    <Pie
                        data={data}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="count"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                        {data.map((entry, index) => (
                            <Cell key={`cell-${index}`}
                                  fill={COLOR_BY_NAME[entry.name] || grey[400]} />
                        ))}
                    </Pie>
                    <Tooltip />
                </PieChart>
            </ResponsiveContainer>
        </ChartContainer>
    );
}

// =============================================================================
// COMPONENTE: Histórico de Downtime
// =============================================================================
export function DowntimeHistory() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await api.get('/provisioning/dashboard/downtime_history');
                setData(response.data);
            } catch (error) {
                console.error('Erro ao buscar downtime:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) return <LoadingCard height={350} />;

    return (
        <ChartContainer title="Incidentes por Dia (30 dias)">
            <ResponsiveContainer height={300} width="100%">
                <BarChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="incidents" fill={red[600]} radius={[4, 4, 0, 0]} />
                </BarChart>
            </ResponsiveContainer>
        </ChartContainer>
    );
}

// =============================================================================
// COMPONENTES AUXILIARES
// =============================================================================

// Container padrão para gráficos
function ChartContainer({ title, children }) {
    return (
        <Paper elevation={2} sx={{ p: 2, height: 'fit-content' }}>
            <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                    fontFamily: 'Montserrat', 
                    fontWeight: 500,
                    mb: 2
                }}
            >
                {title}
            </Typography>
            {children}
        </Paper>
    );
}

// Card para status individual
function StatusCard({ icon, value, label, color, percentage }) {
    return (
        <Box textAlign="center">
            {icon && <Box mb={1}>{icon}</Box>}
            <Typography 
                variant="h4" 
                sx={{ 
                    fontWeight: 'bold',
                    color: color || 'text.primary',
                    fontFamily: 'Montserrat'
                }}
            >
                {value}
            </Typography>
            <Typography variant="body2" color="textSecondary">
                {label}
                {percentage && ` (${percentage}%)`}
            </Typography>
        </Box>
    );
}

// Loading genérico
function LoadingCard({ height = 200 }) {
    return (
        <Paper 
            elevation={2} 
            sx={{
                height,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2
            }}
        >
            <CircularProgress />
        </Paper>
    );
}